import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from './ui/sonner';
import { Button } from './ui/button';
import { MEETINGS } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MeetingsList() {
  const [records, setRecords] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchRecords = () => {
    axios
      .get(`${API}/meetings`, { withCredentials: true })
      .then(({ data }) => setRecords(data.records || []))
      .catch(() => setRecords([]));
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/meetings/${id}`, { withCredentials: true });
      setRecords((old) => old.filter((r) => r.id !== id));
      toast.success('会议记录已删除');
    } catch (e) {
      toast.error('删除失败');
    }
  };

  const handleExport = async (id, roomId) => {
    try {
      const res = await axios.get(`${API}/meetings/${id}/export`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meeting-summary-${roomId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('导出失败');
    }
  };

  if (records === null) {
    return <p className="font-body text-sm text-slate-400">加载中…</p>;
  }

  if (records.length === 0) {
    return (
      <p data-testid={MEETINGS.emptyState} className="font-body text-sm text-slate-400">
        暂无会议记录
      </p>
    );
  }

  return (
    <div data-testid={MEETINGS.container} className="space-y-3">
      {records.map((r) => (
        <div
          key={r.id}
          data-testid={MEETINGS.row(r.id)}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-base font-semibold text-slate-900">{r.title}</p>
              <p className="mt-1 font-mono text-xs text-slate-400">
                房间 {r.room_id} · 主持人 {r.host_name} · {new Date(r.ended_at).toLocaleString('zh-CN')}
              </p>
              <p className="mt-1 truncate font-body text-xs text-slate-500">
                参会人：{r.participants.join('、') || '未知'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
              <button
                data-testid={MEETINGS.summaryToggle(r.id)}
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                {expanded === r.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <Button
                data-testid={MEETINGS.exportBtn(r.id)}
                variant="outline"
                size="icon"
                onClick={() => handleExport(r.id, r.room_id)}
                className="h-10 w-10 rounded-full border-slate-200 text-slate-600 hover:bg-slate-100"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                data-testid={MEETINGS.deleteBtn(r.id)}
                variant="outline"
                size="icon"
                onClick={() => handleDelete(r.id)}
                className="h-10 w-10 rounded-full border-slate-200 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {expanded === r.id && (
            <div className="mt-3 space-y-3">
              <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 font-body text-sm text-slate-600">
                {r.summary || '无AI摘要'}
              </div>
              {r.evaluations && r.evaluations.length > 0 && (
                <div>
                  <p className="mb-2 font-heading text-sm font-semibold text-slate-700">发言表现评估</p>
                  <div className="space-y-2">
                    {r.evaluations.map((ev, idx) => (
                      <div
                        key={`${r.id}-${idx}`}
                        data-testid={MEETINGS.evaluationItem(r.id, idx)}
                        className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-body text-sm font-medium text-slate-900">{ev.name}</p>
                          <p className="mt-0.5 font-body text-xs text-slate-500">{ev.comment}</p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-1 font-mono text-xs font-bold ${
                            ev.score >= 7
                              ? 'bg-emerald-100 text-emerald-700'
                              : ev.score >= 4
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {ev.score.toFixed(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
